import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Repeat, Loader2, Trash2, Pause, Play, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { describeDays, formatLocalTime } from '@dealcloser/shared'
import { useAutoSchedules, useUpdateAutoSchedule, useDeleteAutoSchedule } from './use-content'
import { CONTENT_TYPE_LABELS, TONE_LABELS } from './types'
import type { ContentType, ContentTone } from './types'

/**
 * The user's recurring auto-post campaigns (created from a reviewed post in
 * the generate dialog). Pause / resume / delete inline; the weekly slot
 * meter shows how much of the plan's auto-post allowance is committed.
 *
 * 2026-10-03: always rendered, with a "New auto-post campaign" entry point
 * and an empty state. It used to return null until the first campaign
 * existed, so the feature was invisible unless you happened to click
 * "Generate Post" and then the third button. Pausing or deleting now also
 * returns the campaign's queued posts to drafts (server side).
 */
export function AutoSchedulesCard({ onCreate }: { readonly onCreate?: () => void }) {
  const { data, isLoading } = useAutoSchedules()
  const update = useUpdateAutoSchedule()
  const remove = useDeleteAutoSchedule()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const schedules = data?.schedules ?? []
  const allowance = data?.weekly_allowance ?? 0
  const used = data?.weekly_slots_used ?? 0
  const posterSlots = data?.poster_slots ?? 0
  const full = data !== undefined && used >= allowance

  function unqueuedNote(n: number | undefined): string | null {
    if (!n) return null
    return `${n} queued post${n === 1 ? '' : 's'} moved back to drafts.`
  }

  async function toggle(id: string, active: boolean) {
    setBusyId(id)
    setNotice(null)
    try {
      const res = await update.mutateAsync({ id, updates: { active } })
      setNotice(unqueuedNote(res.unscheduled_posts))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not update the campaign')
    } finally {
      setBusyId(null)
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!window.confirm(`Delete the auto-post campaign "${name}"? Published posts stay in your list; posts it queued but has not published yet move back to drafts.`)) return
    setBusyId(id)
    setNotice(null)
    try {
      const res = await remove.mutateAsync(id)
      setNotice(unqueuedNote(res.unscheduled_posts))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Repeat className="h-4 w-4 text-cobalt-bright" />
          <p className="text-sm font-semibold">Auto-post campaigns</p>
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <p className="text-xs text-muted-foreground" data-testid="auto-slot-meter">
              {used} of {allowance} automated post{allowance === 1 ? '' : 's'} per week in use
            </p>
          )}
          {onCreate && (
            <Button size="sm" className="h-8 gap-1 text-xs" onClick={onCreate} disabled={full} data-tour="content-auto-new">
              <Plus className="h-3.5 w-3.5" /> New auto-post campaign
            </Button>
          )}
        </div>
      </div>
      {(posterSlots > 0 || (full && allowance <= 1)) && (
        <p className="border-b px-4 py-2 text-xs text-muted-foreground">
          {posterSlots > 0 && (
            <>
              The LinkedIn Poster in{' '}
              <Link to="/settings?tab=voice-content" className="text-cobalt-bright hover:underline">Settings, Voice &amp; Content</Link>{' '}
              uses {posterSlots} of these.{' '}
            </>
          )}
          {full && allowance <= 1 && 'The Basic plan includes 1 automated post per week. Pro includes up to 7, split across as many campaigns as you like.'}
        </p>
      )}
      {notice && <p className="border-b px-4 py-2 text-xs text-emerald-400">{notice}</p>}
      {isLoading ? (
        <div className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : schedules.length === 0 ? (
        <div className="px-4 py-4 text-xs text-muted-foreground">
          <p>
            Pick a topic, format and tone once. You review a sample post first, then choose the days and time
            (for example Mon, Wed, Fri at 9:00 AM) and a fresh post in that style is written for each slot and published automatically, or held as a draft for your approval if you prefer.
          </p>
          {allowance > 0 && (
            <p className="mt-1">
              Your plan includes {allowance} automated post{allowance === 1 ? '' : 's'} per week.
            </p>
          )}
        </div>
      ) : (
        <ul className="divide-y">
          {schedules.map((s) => {
            const next = s.next_run_at ? new Date(s.next_run_at) : null
            return (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0 space-y-0.5">
                  <p className="truncate text-sm font-medium">
                    {s.name}
                    {!s.active && <span className="ml-2 rounded-full border px-1.5 py-0.5 text-[10px] text-muted-foreground">Paused</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {describeDays(s.days_of_week)} at {formatLocalTime(s.hour_local, s.minute_local)} ({s.timezone})
                    {' · '}
                    {CONTENT_TYPE_LABELS[s.content_type as ContentType] ?? s.content_type}
                    {s.tone ? ` · ${TONE_LABELS[s.tone as ContentTone] ?? s.tone}` : ''}
                    {' · '}
                    {s.auto_approve ? 'publishes automatically' : 'drafts for review'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {s.active && next ? `Next post ${next.toLocaleString()}` : s.active ? 'No upcoming slot' : 'Paused'}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1 text-xs"
                    onClick={() => void toggle(s.id, !s.active)}
                    disabled={busyId === s.id}
                  >
                    {busyId === s.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : s.active ? (
                      <><Pause className="h-3 w-3" /> Pause</>
                    ) : (
                      <><Play className="h-3 w-3" /> Resume</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs text-destructive hover:text-destructive"
                    onClick={() => void handleDelete(s.id, s.name)}
                    disabled={busyId === s.id}
                    aria-label={`Delete ${s.name}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
